import { QueryClient } from '@tanstack/react-query'

/**
 * Shared React Query client singleton.
 *
 * Exported from a standalone module (rather than created inline in App.tsx) so
 * non-React code — most importantly the auth store's `logout()` action — can
 * reach the same cache instance and clear it. Without this, a previous user's
 * cached data (e.g. the My Learning course lists) survives a logout/login in
 * the same tab and leaks to the next account, because the in-memory cache is
 * never reset. See `useLearningItems` for the complementary per-user query keys.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: (failureCount, error) => {
        // Don't retry on auth errors — the interceptor handles token refresh,
        // and a 403 (forbidden / suspended tenant) never succeeds on retry.
        const status = (error as { status?: number })?.status;
        if (status === 401 || status === 403) return false;
        return failureCount < 3;
      },
    },
  },
})
